// French copy, namespace `shell` (source language: English is typed against it).
// The product frame: rail, topbar, offer switcher, running pass, ⌘K palette,
// end-of-pass notification and the guided tour of the first session.

/** Time since a pass started, in the topbar. */
const elapsed = (minutes: number) => (minutes < 1 ? "à l’instant" : `${minutes} min`);

const forOffer = (offer: string | null) => (offer ? ` pour ${offer}` : "");

export const shell = {
  nav: {
    label: "Navigation principale",
    home: "Hune — vue d’ensemble",
    overview: "Vue d’ensemble",
    leads: "Leads",
    reports: "Passages",
    offers: "Mes offres",
    settings: "Paramètres",
    admin: "Administration",
    signOut: "Déconnexion",
  },
  workspace: {
    readOnly: "Lecture seule",
    label: "Espace consulté",
    mine: "Mon espace",
  },
  topbar: {
    unavailable: "Offres et compte indisponibles.",
    account: "Mon compte",
  },
  plan: {
    names: { free: "Gratuit", solo: "Solo", pro: "Pro" },
    current: (name: string) => `Plan ${name}`,
    upgrade: "Passer à Solo",
    upgradeShort: "Plans",
  },
  offerSwitcher: {
    label: "Offre consultée",
    placeholder: "Choisir une offre",
    add: "Ajouter une offre",
  },
  runIndicator: {
    completed: "Passage terminé",
    failed: "Passage interrompu",
    stalled: "Passage sans nouvelles",
    radarSearching: "La veille cherche",
    agentSearching: "L’agent cherche",
    seeOutcome: "Voir le bilan",
    seeReason: "Voir la raison",
    elapsed,
    noSignSince: (minutes: number) => `Aucun signe depuis ${elapsed(minutes)}`,
    background: "L’agent travaille côté serveur : vous pouvez naviguer ou fermer l’onglet, le passage continue.",
  },
  passNotification: {
    interrupted: (offer: string | null) => `Passage interrompu${forOffer(offer)}`,
    interruptedBody: "Ouvrez Hune pour voir la raison et relancer.",
    completed: (offer: string | null) => `Passage terminé${forOffer(offer)}`,
    nothingNewBody: "Rien de neuf cette fois : les signaux portaient sur des organisations déjà suivies.",
    newLeads: (count: number, offer: string | null) =>
      `${count} ${count > 1 ? "nouveaux leads" : "nouveau lead"}${forOffer(offer)}`,
    reactivated: (count: number, offer: string | null) =>
      `${count} ${count > 1 ? "leads réactivés" : "lead réactivé"}${forOffer(offer)}`,
    /** `names`: up to two organisations, already joined; `rest`: how many more. */
    names: (names: string, rest: number) =>
      rest > 0 ? `${names} et ${rest} autre${rest > 1 ? "s" : ""}.` : `${names}.`,
    alsoReactivated: (count: number) => `${count} ${count > 1 ? "leads réactivés" : "lead réactivé"}.`,
  },
  palette: {
    search: "Rechercher",
    title: "Rechercher dans Hune",
    description: "Aller à un lead, un interlocuteur, un passage ou un écran",
    placeholder: "Un lead, un interlocuteur, une action…",
    empty: "Rien ne correspond.",
    leads: "Leads",
    recentLeads: "Leads récents",
    contacts: "Interlocuteurs",
    passes: "Passages",
    passReport: "Rapport du passage",
    actions: "Actions",
    launchPass: "Lancer un passage",
    /** Extra search words, separated by spaces. */
    launchKeywords: "analyse veille",
    switchOffer: (name: string) => `Passer à l’offre ${name}`,
    offerKeyword: "offre",
    addOffer: "Ajouter une offre",
    exportLeads: "Exporter les leads (CSV)",
    exportKeywords: "csv excel sheets télécharger",
    exportFailed: "L’export a échoué.",
    goTo: "Aller à",
    goToPage: (page: string) => `Aller à ${page}`,
  },
  /** Lead statuses, keyed by the stored value. */
  leadStatuses: {
    new: "Nouveau",
    to_contact: "À contacter",
    contacted: "Contacté",
    replied: "A répondu",
    won: "Gagné",
    dismissed: "Écarté",
  },
  tour: {
    welcome: {
      running: {
        title: "Votre première analyse a commencé",
        body: "Pendant que l’agent cherche, voici comment Hune travaille.",
        note: "Vous pouvez fermer : l’analyse continue.",
      },
      settled: {
        title: "Votre première analyse est terminée",
        body: "Son bilan vous attend sur cette page. Avant de l’ouvrir, voici comment Hune travaille.",
        note: "La visite dure environ une minute.",
      },
    },
    /** Keyed by the step id of `TOUR_STEPS`; a `running`/`settled` pair when the text depends on the first pass. */
    steps: {
      passage: {
        title: { running: "L’agent explore votre marché", settled: "Le bilan de votre premier passage" },
        body: {
          running:
            "Ces étapes suivent son travail réel : recherches lancées, pages lues, signaux retenus. À la fin, cette carte affiche le bilan : nouveaux leads, réactivés, déjà connus.",
          settled:
            "Chaque passage se résume ici : nouveaux leads, réactivés, déjà connus. Le rapport complet et la source de chaque signal restent dans « Passages ».",
        },
      },
      leads: {
        title: "Vos leads arrivent ici",
        body: "Chaque lead : une organisation, un signal daté et sourcé, les bons interlocuteurs. Hune ne contacte personne : c’est vous qui faites avancer chaque lead.",
      },
      ciblage: {
        title: "Vous orientez l’agent",
        body: "L’agent déduit votre cible de votre site : corrigez-la dans « Mes offres », votre version fait foi. Écartez un lead avec une raison, il en tiendra compte au passage suivant.",
      },
      veille: {
        title: "Ensuite, la veille prend le relais",
        body: {
          running:
            "Après ce premier passage, l’agent repassera seul sur votre marché et ne vous signalera que du nouveau. Le passage en cours continue même si vous fermez l’onglet.",
          settled:
            "L’agent repasse seul sur votre marché et ne vous signale que du nouveau : rien à relancer. Cet interrupteur met la veille en pause.",
        },
      },
    },
    lastAction: { running: "Revenir au passage", settled: "Voir le bilan" },
    counter: (step: number, total: number) => `${step} sur ${total}`,
    next: "Voir la suite",
    skip: "Passer la visite",
    close: "Fermer",
    nudge: {
      title: "Votre premier bilan est prêt",
      none: "Aucun lead retenu cette fois. Vérifiez ici le ciblage que l’agent a déduit de votre site : le prochain passage partira de votre version.",
      leads: (count: number) =>
        count === 1
          ? "1 nouveau lead, avec son signal et ses sources. Ouvrez-le ici, puis faites-le avancer ou écartez-le avec une raison."
          : `${count} nouveaux leads, chacun avec son signal et ses sources. Ouvrez-les ici, faites avancer ceux qui comptent et écartez les autres avec une raison.`,
    },
  },
  errors: {
    signIn: "Connectez-vous.",
  },
};
